import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { Translate } from "./types.js";

export type ToastKind = "info" | "error";
interface ToastItem {
  id: number;
  kind: ToastKind;
  message: string;
}

export interface ToastApi {
  /** Shows a short message; errors stay a little longer. */
  show(message: string, kind?: ToastKind): void;
}

const ToastContext = createContext<ToastApi | null>(null);

/** Access toasts from any component under <ToastProvider>. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>");
  return ctx;
}

export function ToastProvider(props: { t: Translate; children: ReactNode }): ReactNode {
  const [items, setItems] = useState<ToastItem[]>([]);
  const next = useRef(1);
  const dismiss = useCallback(
    (id: number) => setItems((all) => all.filter((i) => i.id !== id)),
    [],
  );
  const show = useCallback(
    (message: string, kind: ToastKind = "info") => {
      const id = next.current++;
      setItems((all) => [...all.slice(-2), { id, kind, message }]);
      window.setTimeout(() => dismiss(id), kind === "error" ? 7000 : 4000);
    },
    [dismiss],
  );
  const api = useMemo<ToastApi>(() => ({ show }), [show]);
  return (
    <ToastContext.Provider value={api}>
      {props.children}
      <div className="toast-region" role="status" aria-live="polite">
        {items.map((item) => (
          <div key={item.id} className={`toast${item.kind === "error" ? " toast-error" : ""}`}>
            <span>{item.message}</span>
            <button type="button" onClick={() => dismiss(item.id)}>
              {props.t("ui.toast.dismiss")}
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
